import React from 'react';
import { map, round } from 'lodash';
import Tippy from '@tippy.js/react';
import { IconInfo, IconBatteryHighStatus, IconBatteryHalfStatus, IconBatteryLowStatus } from 'svgIcons/MotionPortalIcons';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { sensorFeatureKeys, batteryModeOperation } from '../../constants';
import {
    formatSensorFeatureValue,
    getOperatingModeTypeLabel,
    getRequestStatusText,
    getResponseStatusText,
    getColumns,
    getColumnsForRssiValue
} from '../helpers';
import { ITEMS_PER_PAGE, ITEM_BATTERY_LEVEL, ITEM_RSSI_FEATURE, ITEM_SENSOR_BATTERY_LEVEL } from '../constants';
import { Battery, GeneralDialog, GeneralTable } from 'sharedComponents';
import { sortingDirections } from 'sharedComponents/GeneralTable';
import colors from 'theme/_colors.scss';


const SensorFeaturesList = ({ visibleFeatures, timeseriesInfo, sensorRssiValue, show, setshow, isFirmwareUpdateEnforced, sensorBatteryLevel }) => {
    const batteryValue = timeseriesInfo[0]?.timeseries[0]?.timeseriesValue?.v;
    const batteryUnits = timeseriesInfo[0]?.timeseries[0]?.timeseriesValue?.u;


    const { t: translate } = useTranslation();
    const toggleShow = () => {
        setshow(false);
    };
    const featuresToRender = [...visibleFeatures];
    if (batteryValue !== undefined && !visibleFeatures.some(f => f.featureKeyName === ITEM_BATTERY_LEVEL)) {
        featuresToRender.unshift({
            featureKeyName: ITEM_BATTERY_LEVEL,
            featureValue: batteryValue,
            featureUnit: batteryUnits,
        });
    }
    if (sensorRssiValue &&
        sensorRssiValue.length > 0 &&
        !visibleFeatures.some(f => f.featureKeyName === ITEM_RSSI_FEATURE)) {
        featuresToRender.unshift({
            featureKeyName: ITEM_RSSI_FEATURE,
            featureValue: sensorRssiValue,
            featureUnit: 'db',
        });
    }
    const batteryModeFeature = visibleFeatures.find(f => f.featureKeyName === sensorFeatureKeys.BatteryMode);
    const batteryModeIndex = featuresToRender.findIndex(f => f.featureKeyName === sensorFeatureKeys.BatteryMode);
    if (batteryModeIndex !== -1) {
        featuresToRender.splice(batteryModeIndex, 1);
    }
    const batteryLevelIndex = featuresToRender.findIndex(f => f.featureKeyName === sensorFeatureKeys.BatteryLevel);
    if (batteryLevelIndex !== -1) {
        featuresToRender.splice(batteryLevelIndex, 1);
    }

    const batteryLevel = sensorBatteryLevel?.batteryLevel;
    const batteryMode = batteryModeFeature?.featureValue;
    const levelAvailable = batteryLevel !== null && batteryLevel !== undefined && batteryLevel !== '';
    const modeAvailable = batteryMode !== null && batteryMode !== undefined && batteryMode !== '';

    // Show the battery status row if either mode or level is available.
    // The rendering logic inside `getValueForFeatureKey` will handle the different combinations.
    if (modeAvailable || levelAvailable) {
        featuresToRender.push({
            featureKeyName: sensorFeatureKeys.BatteryStatus,
            featureValue: {
                level: batteryLevel,
                mode: batteryMode,
            },
        });
    }

    const getValueForFeatureKey = (feature) => {
        switch (feature.featureKeyName) {
            case sensorFeatureKeys.BatteryPercentage:
                return feature.featureValue ? <Battery value={feature.featureValue} /> : '-';
            case sensorFeatureKeys.PowerSavingMode:
                return feature.featureValue === '1' ?
                    translate('ABB.Powertrain.Frontend.powerSavingModeEnabled') :
                    translate('ABB.Powertrain.Frontend.powerSavingModeDisabled');
            case sensorFeatureKeys.OperatingMode:
                return translate(getOperatingModeTypeLabel(feature.featureValue));
            case sensorFeatureKeys.RawDataCollectionRequest:
            case sensorFeatureKeys.OnDemandDataCollectionRequest:
                return getRequestStatusText(feature.featureValue, translate);
            case sensorFeatureKeys.SensorRSSIValue:
                return feature.featureValue && feature.featureValue.rssiValue ?
                    <div className='notification-type-RSSI'>
                        <Tippy
                            content={translate('ABB.Powertrain.Frontend.sensorFeatureRSSIValueDescription')}
                            interactive={true}
                            interactiveBorder={10}
                            animation='scale'
                            theme='light-border'
                            trigger='click'
                            placement='left'>
                            <div className='info-icon'>
                                <IconInfo width={'14px'} height={'14px'} color={'black'} />
                                {feature.featureValue.rssiValue + ' dBm'}
                            </div>
                        </Tippy>
                    </div> : '-';
            case sensorFeatureKeys.BatteryStatus: {
                const { level, mode } = feature.featureValue;
                const levelAvailable = level !== null && level !== undefined && level !== '';
                const modeAvailable = mode !== null && mode !== undefined && mode !== '';

                const getBatteryIcon = (value) => {
                    if (value > 50) { return IconBatteryHighStatus; }
                    if (value > 20) { return IconBatteryHalfStatus; }
                    return IconBatteryLowStatus;
                };

                const BatteryIcon = levelAvailable ? getBatteryIcon(level) : null;

                let content = null;

                if (modeAvailable && levelAvailable) {
                    const modeText = mode === batteryModeOperation.NORMAL ? null : translate(`ABB.Powertrain.Frontend.sensorFeatureBatteryModeLabel${mode}`);
                    content = <>
                        {BatteryIcon && <BatteryIcon />}
                        <span className='battery-level-value'>{`${round(level)}%`}</span>
                        {modeText && <span className='battery-separator'>|</span>}
                        {modeText && <span>{modeText}</span>}
                        {modeText && <Tippy
                            content={translate(`ABB.Powertrain.Frontend.sensorFeatureBatteryModeTooltip${mode}`)}
                            interactive={true}
                            interactiveBorder={10}
                            animation='scale'
                            theme='light-border'
                            trigger='click'
                            placement='top'>
                            <span className='info-icon'>
                                <IconInfo width={'14px'} height={'14px'} />
                            </span>
                        </Tippy>}
                    </>;
                } else if (modeAvailable) {
                    content = <>
                        <span>{translate(`ABB.Powertrain.Frontend.sensorFeatureBatteryModeLabel${mode}`)}</span>
                        <Tippy
                            content={translate(`ABB.Powertrain.Frontend.sensorFeatureBatteryModeTooltip${mode}`)}
                            interactive={true}
                            interactiveBorder={10}
                            animation='scale'
                            theme='light-border'
                            trigger='click'
                            placement='top'>
                            <span className='info-icon'>
                                <IconInfo width={'14px'} height={'14px'} />
                            </span>
                        </Tippy>
                    </>;
                } else if (levelAvailable) {
                    content = <>
                        {BatteryIcon && <BatteryIcon />}
                        <span className='battery-level-value'>{`${round(level)}%`}</span>
                    </>;
                }

                return content ? <div className='notification-type-degraded-mode'>{content}</div> : null;
            }
            case ITEM_BATTERY_LEVEL:
                return <div>{feature?.featureValue?.toFixed(2)} {feature?.featureUnit}</div>;
            case ITEM_SENSOR_BATTERY_LEVEL: {
                const batteryLevelDisplay = feature.featureValue !== null && feature.featureValue !== undefined
                    ? `${round(feature.featureValue)}%`
                    : '-';
                const getBatteryIcon = (value) => {
                    if (value > 50) { return IconBatteryHighStatus; }
                    if (value > 20) { return IconBatteryHalfStatus; }
                    return IconBatteryLowStatus;
                };
                const BatteryIcon = feature.featureValue !== null && feature.featureValue !== undefined
                    ? getBatteryIcon(feature.featureValue)
                    : IconBatteryLowStatus;
                return <div className='notification-type-degraded-mode'>
                    {feature.batteryModeValue && <BatteryIcon />}
                    <span className='battery-level-value'>{batteryLevelDisplay}</span>
                </div>;
            }
            case sensorFeatureKeys.RawDataCollectionResponseMessage:
            case sensorFeatureKeys.OnDemandDataCollectionResponseMessage:
                return getResponseStatusText(feature.featureValue, translate);

            case sensorFeatureKeys.BatteryLevel: {
                const roundedBatteryLevel = round(feature.featureValue);
                return roundedBatteryLevel || roundedBatteryLevel === 0 ? `${roundedBatteryLevel} %` : '-';
            }
            case ITEM_RSSI_FEATURE:
                return (
                    <div>
                        <button className='view-details' onClick={() => setshow(true)}>
                            {translate('ABB.Powertrain.Frontend.MsrViewDetails')}
                        </button>
                        {show &&
                            <GeneralDialog
                                show={show}
                                close={toggleShow}
                                title={translate('ABB.Powertrain.Frontend.rssiAvgValueModelTitle')}
                                closeButton={true}
                            >
                                <GeneralTable
                                    className='msr-details-table'
                                    columns={getColumnsForRssiValue(translate)}
                                    data={sensorRssiValue} // <-- Pass the sensorRssiValue list here
                                    paginationProps={{ pageSize: ITEMS_PER_PAGE, hideOnSinglePage: true }}
                                    initialSortingProps={{
                                        direction: sortingDirections.DESC
                                    }}
                                />
                            </GeneralDialog>
                        }
                    </div>
                );
            case sensorFeatureKeys.MsrDetails:
                return <div>
                    <button className='view-details' onClick={() => setshow(true)}>{translate('ABB.Powertrain.Frontend.MsrViewDetails')}</button>
                    {show && <GeneralDialog
                        show={show}
                        close={toggleShow}
                        title={translate('ABB.Powertrain.Frontend.MsrCommissioningDetails')}
                        closeButton={true}
                    >
                        <GeneralTable
                            className='msr-details-table'
                            columns={getColumns(translate)}
                            data={feature?.featureValueObject?.ChannelMappings}
                            paginationProps={{ pageSize: ITEMS_PER_PAGE, hideOnSinglePage: true }}
                            initialSortingProps={{
                                direction: sortingDirections.DESC
                            }} />
                    </GeneralDialog>}
                </div>;
            case sensorFeatureKeys.FirmwareVersion:
                return <div>
                    {feature.featureValue}
                    {isFirmwareUpdateEnforced ?
                        <div>
                            <div className='error-message-firmware-update'>
                                <Tippy
                                    content={translate('ABB.Powertrain.Frontend.sensorDetailsFirmwareUpdateDescriptionText')}
                                    interactive={true}
                                    interactiveBorder={10}
                                    animation='scale'
                                    theme='light-border'
                                    trigger='click'
                                    placement='bottom'
                                >
                                    <div className='info-icon'>
                                        <IconInfo width={'14px'} height={'14px'} color={colors.torchRed} />
                                    </div>
                                </Tippy>
                                <div className='error-message-text'>{translate('ABB.Powertrain.Frontend.sensorDetailsFirmwareUpdateText')}</div>
                            </div>
                        </div>
                        : null}
                </div>;
            default:
                return formatSensorFeatureValue(feature, translate);
        }
    };

    // return map(visibleFeatures, (el, index) => {
    //     return (
    //         <div key={index} className='sensor-properties-list-item'>
    //             <div className='data-label'>{translate(`ABB.Powertrain.Frontend.sensorFeature_${el.featureKeyName}`) || el.featureDisplayName}</div>
    //             <div className='data-value'>{getValueForFeatureKey(el, timeseriesInfo[0])}</div>
    //         </div>
    //     );
    // });
    return map(featuresToRender, (el, index) => {
        const value = getValueForFeatureKey(el);
        const isBatteryStatus = el.featureKeyName === sensorFeatureKeys.BatteryStatus;
        // If getValueForFeatureKey returns null, it means we should not render this feature.
        if (value === null) {
            return null;
        }
        return (
            <div key={index} className={`sensor-properties-list-item${isBatteryStatus ? ' battery-status-list-item' : ''}`}>
                <div className='data-label'>{translate(`ABB.Powertrain.Frontend.sensorFeature_${el.featureKeyName}`) || el.featureDisplayName}</div>
                <div className='data-value'>{value}</div>
            </div>
        );
    });
};

export default SensorFeaturesList;

SensorFeaturesList.propTypes = {
    visibleFeatures: PropTypes.array,
    show: PropTypes.bool,
    setshow: PropTypes.func,
    isFirmwareUpdateEnforced: PropTypes.bool,
    sensorBatteryLevel: PropTypes.object,
};
